import { gql } from 'graphql-tag';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { useQuery } from 'urql';
import { useAbilityContext } from '~/components/Can';
import { NavItemsBar } from '~/components/nav/NavItemsBar';
import {
  NavSettingsMenuItem,
  NavSettingsPanel,
} from '~/components/ui/NavSettingsPanel';
import { MenuItem, TopBar } from '~/components/ui/TopBar';
import { useLocalStorage } from '~/hooks';
import { useBreakpoint } from '~/hooks/useBreakpoint';
import { buildNavItems } from '~/utils/nav/buildNavItems';

type AssetsNavProps = {
  onViewChange?: (view: string) => void;
  initialView?: string;
  onNavItemsChange?: (navItems: string[]) => void;
};

const ALL = 'All' as const;
const OTHER = 'Other' as const;
const EVT_ASSET_TYPE_RENAMED = 'assetTypeRenamed';
const STORAGE_KEYS = {
  view: 'nav.assets.view',
  settings: 'nav.assets.settings',
  nav: 'nav.assets.items',
} as const;
const titleForType = (type: string) => {
  if (type === ALL) return 'All';
  if (type === OTHER) return 'Other';
  return type;
};

type AssetTypeConfig = {
  types: string[];
  orderMode: 'ALPHA' | 'MANUAL';
  navTypes: string[];
  showAllInNav: boolean;
  isCustomized: boolean;
};

const AssetTypeConfigDocument = gql`
  query AssetTypeConfig_Nav {
    assetTypeConfig {
      types
      orderMode
      navTypes
      showAllInNav
      isCustomized
    }
  }
`;

export const AssetsNav = ({
  onViewChange,
  initialView,
  onNavItemsChange,
}: AssetsNavProps = {}) => {
  const { t } = useTranslation();
  const localStorage = useLocalStorage();
  const ability = useAbilityContext();
  const isOwner = ability.can('manage', 'Account');

  const [view, setView] = useState(initialView || '');
  const updateView = useCallback((newView: string) => {
    setView(newView);
  }, []);
  useEffect(() => {
    // Notify parent AFTER commit to avoid render-phase updates in parent
    if (onViewChange && view) onViewChange(view);
  }, [view, onViewChange]);

  const setViewAndPersist = useCallback(
    (newView: string) => {
      updateView(newView);
      try {
        localStorage.set(STORAGE_KEYS.view, newView);
      } catch {
        void 0;
      }
    },
    [updateView, localStorage]
  );

  const [configResult] = useQuery<{ assetTypeConfig: AssetTypeConfig }>({
    query: AssetTypeConfigDocument,
    requestPolicy: 'cache-and-network',
  });
  const serverTypes = useMemo(
    () => configResult.data?.assetTypeConfig?.types ?? [],
    [configResult.data?.assetTypeConfig?.types]
  );
  const savedNav = useMemo(
    () => configResult.data?.assetTypeConfig?.navTypes ?? [],
    [configResult.data?.assetTypeConfig?.navTypes]
  );
  const showAllInNavServer = configResult.data?.assetTypeConfig?.showAllInNav;
  const [showAllInNav, setShowAllInNav] = useState<boolean>(true);
  useEffect(() => {
    if (typeof showAllInNavServer === 'boolean')
      setShowAllInNav(showAllInNavServer);
  }, [showAllInNavServer]);
  const orderMode = configResult.data?.assetTypeConfig?.orderMode;

  // Lightweight counts instead of loading all assets
  const AssetTypeCountsDocument = gql`
    query AssetTypeCounts_Nav {
      assetTypeCounts {
        type
        count
      }
      hasUntypedAssets
    }
  `;
  const [countsResult] = useQuery<{
    assetTypeCounts: { type: string | null; count: number }[];
    hasUntypedAssets: boolean;
  }>({
    query: AssetTypeCountsDocument,
    requestPolicy: 'cache-and-network',
  });
  const typesInUse = useMemo(() => {
    const counts = countsResult.data?.assetTypeCounts ?? [];
    const list = counts.map((c) => c.type).filter((t): t is string => !!t);
    return Array.from(new Set(list));
  }, [countsResult.data]);
  const hasUntyped = useMemo(() => {
    const counts = countsResult.data?.assetTypeCounts ?? [];
    return (
      countsResult.data?.hasUntypedAssets === true ||
      counts.some((c) => !c.type)
    );
  }, [countsResult.data]);

  const navItems = useMemo<string[]>(() => {
    return buildNavItems({
      savedNav,
      configuredValues: serverTypes,
      typesInUse,
      hasUntyped,
      showAllInNav,
      orderMode: orderMode ?? 'ALPHA',
      topN: 6,
    });
  }, [savedNav, showAllInNav, serverTypes, typesInUse, hasUntyped, orderMode]);

  // Displayed nav items with immediate fallback from cache/local storage
  const [displayedNavItems, setDisplayedNavItems] = useState<string[]>(() => {
    try {
      const raw = localStorage.get(STORAGE_KEYS.nav) as string | null;
      const parsed = raw ? (JSON.parse(raw) as unknown) : null;
      if (Array.isArray(parsed) && parsed.every((v) => typeof v === 'string')) {
        return parsed as string[];
      }
    } catch {
      // ignore
    }
    // Safe minimal fallback to avoid empty UI while queries load
    return [ALL];
  });

  // When computed navItems are ready, update displayed list and persist
  useEffect(() => {
    if (!navItems || navItems.length === 0) return;
    setDisplayedNavItems((prev) =>
      prev.join('|') === navItems.join('|') ? prev : navItems
    );
    try {
      localStorage.set(STORAGE_KEYS.nav, JSON.stringify(navItems));
    } catch {
      void 0;
    }
  }, [navItems, localStorage]);

  // Notify parent on displayed items change (so consumers get immediate items)
  useEffect(() => {
    onNavItemsChange?.(displayedNavItems);
  }, [displayedNavItems, onNavItemsChange]);

  useEffect(() => {
    if (!view && displayedNavItems.length > 0) {
      try {
        const saved =
          (localStorage.get(STORAGE_KEYS.view) as string | null) || '';
        const initial =
          saved && displayedNavItems.includes(saved)
            ? saved
            : displayedNavItems[0];
        updateView(initial);
      } catch {
        updateView(displayedNavItems[0]);
      }
    }
  }, [view, displayedNavItems, localStorage, updateView]);

  // Keep current tab when an asset type is renamed elsewhere
  useEffect(() => {
    const onRenamed = (e: Event) => {
      const detail = (e as CustomEvent).detail as
        | { from?: string; to?: string }
        | undefined;
      if (!detail) return;
      const { from, to } = detail;
      if (!from || !to) return;
      setView((prev) => {
        if (prev === from) {
          setViewAndPersist(to);
          return to;
        }
        return prev;
      });
    };
    window.addEventListener(EVT_ASSET_TYPE_RENAMED, onRenamed as EventListener);
    return () => {
      window.removeEventListener(
        EVT_ASSET_TYPE_RENAMED,
        onRenamed as EventListener
      );
    };
  }, [setViewAndPersist]);

  // Overflow handled by NavItemsBar

  // Mobile top bar
  const mobileMenuItems: MenuItem[] = useMemo(() => {
    const base = displayedNavItems.map((type) => ({
      key: `asset-type-${type}`,
      label:
        type === ALL
          ? t('allAssets', { defaultValue: `All ${t('asset_plural')}` })
          : titleForType(type),
      isActive: type === view,
      onClick: () => setViewAndPersist(type),
    }));
    if (!isOwner) return base as MenuItem[];
    return [...base];
  }, [displayedNavItems, view, isOwner, setViewAndPersist, t]);

  // Desktop settings panel
  const [showSettings, setShowSettings] = useState(() => {
    try {
      const saved = localStorage.get(STORAGE_KEYS.settings) as string | null;
      return saved === 'true';
    } catch {
      return false;
    }
  });
  const setShowSettingsAndSave = useCallback(
    (newValue: boolean) => {
      setShowSettings(newValue);
      try {
        localStorage.set(STORAGE_KEYS.settings, newValue.toString());
      } catch {
        void 0;
      }
    },
    [localStorage]
  );
  const settingsMenuItems: NavSettingsMenuItem[] = useMemo(
    () => [
      {
        key: 'assets-settings',
        label: 'Asset Types',
        to: '#org-settings-open:assetTypes',
        showPopupIcon: true,
      },
    ],
    []
  );

  const { isMobile } = useBreakpoint();

  if (isMobile) {
    return (
      <TopBar
        title={
          view === ALL
            ? t('myAssets', { defaultValue: 'My Assets' })
            : view
            ? titleForType(view)
            : 'Assets'
        }
        menuItems={mobileMenuItems}
      />
    );
  }

  return (
    <>
      <NavItemsBar
        items={displayedNavItems}
        activeItem={view}
        onSelect={setViewAndPersist}
        labelFor={titleForType}
        recomputeKey={showSettings}
        isPanelOpen={isOwner ? showSettings : undefined}
        rightSlot={
          isOwner ? (
            <NavSettingsPanel
              isOpen={showSettings}
              onToggle={() => setShowSettingsAndSave(!showSettings)}
              menuItems={settingsMenuItems}
              hasItems={true}
            />
          ) : null
        }
        farRightSlot={
          <Link className='add-button' to='new'>
            + {t('asset')}
          </Link>
        }
      />
    </>
  );
};
